// BASE
import { useAppSelector } from 'hooks/redux';
import React, { useMemo } from 'react';

import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { useLoadState } from 'app-components/hooks/utils';

import * as fundingAccountsSelectors from 'data/fundingAccounts/selectors';
import type * as fundingAccountsTypes from 'data/fundingAccounts/types';

//  CUSTOM COMPONENTS
import SimplifiMultiSelect from 'components/SimplifiSelects/SimplifiMultiSelect';

type MenuItem = { name: string; id: string };

export const getItemForFundingAccount = (
  fundingAccount: fundingAccountsTypes.FundingAccount,
  account?: accountsTypes.Account,
) => ({
  name: account ? `${account.name} ${accountsUtils.formatFinalFour(account)}` : 'Account not found',
  id: fundingAccount.id as string,
});

const sortAccounts = (arr: MenuItem[]) =>
  arr.sort((account1, account2) =>
    (account1.name || '').localeCompare(account2.name || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );

interface VerifiedFundingAccountDropdownProps {
  startDroppedDown?: boolean;
  instantFilter?: boolean;
  onSelected: (selected: any[]) => void;
  name?: string;
  selectedItems?: { name: string; id: string }[]; // just an array of fundingAccountIds
  showAllItemsMenu?: boolean;
  sortBy?: string;
  setIsAllSelected?: (val: boolean) => void;
}

/* Component */
const VerifiedFundingAccountDropdown = (props: VerifiedFundingAccountDropdownProps) => {
  const {
    onSelected,
    name = 'Accounts',
    selectedItems,
    showAllItemsMenu,
    startDroppedDown,
    instantFilter,
    sortBy,
    ...other
  } = props;

  const view = useLoadState(fundingAccountsSelectors);

  const fundingAccounts = useAppSelector(fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId);

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  /*
   * Creates the main array of menu items
   * */
  const [menuArray, itemsArray] = useMemo(() => {
    let checkingArray: MenuItem[] = [];
    let savingsArray: MenuItem[] = [];

    fundingAccounts.forEach((fundingAccount, accountId) => {
      const account = accountsById.get(accountId as string);

      if (account?.fundingAccountId === fundingAccount?.id) {
        if (
          account
            ? account.subType === 'SAVINGS' || account.subType === 'CD' || account.subType === 'MONEY_MARKET'
            : fundingAccount.accountType !== 'CHECKING'
        ) {
          savingsArray.push(getItemForFundingAccount(fundingAccount, account));
        } else {
          checkingArray.push(getItemForFundingAccount(fundingAccount, account));
        }
      }
    });

    checkingArray = sortAccounts(checkingArray);
    savingsArray = sortAccounts(savingsArray);

    // total selectable accounts for defaulting to all selection
    const itemsArray = [...checkingArray, ...savingsArray];

    return [
      [
        checkingArray.length ? { name: 'Checking', items: checkingArray } : undefined,
        savingsArray.length ? { name: 'Savings', items: savingsArray } : undefined,
      ].filter((val) => val),
      itemsArray,
    ];
  }, [accountsById, fundingAccounts]);

  return (
    <SimplifiMultiSelect
      viewState={view}
      sharedcomponentid='VERIFIED_FUNDING_ACCOUNT_DROPDOWN'
      startDroppedDown={startDroppedDown}
      menuArray={menuArray}
      instantFilter={instantFilter}
      onSelected={onSelected}
      name={name}
      selectedItems={selectedItems?.length ? selectedItems : itemsArray}
      showAll={showAllItemsMenu}
      {...other}
    />
  );
};

export default VerifiedFundingAccountDropdown;
